import React, {useCallback, useContext, useMemo, useState} from 'react';

import isString from 'lodash.isstring';
import {FormInput} from 'src/components/input';
import {WidthItem} from 'src/components/width-item';
import {MessageContext} from 'src/contexts/message-context';
import {SearchSettingsType as SearchSettingsTypes} from 'src/openapi/admin/models/SearchSettingsType';

import Button from '@pb/kit-component-button';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {useEditSearch} from './useEditSearch';

import type {SearchSettings} from 'src/openapi/admin/models/SearchSettings';
import type {SearchSettingsType} from 'src/openapi/admin/models/SearchSettingsType';


type Props = {
    searchSettings: SearchSettings,
    onClose: () => void,
    onAdd: () => void,
};

export const EditSearchModal = ({onAdd, onClose, searchSettings}: Props) => {
    const [busy, setBusy] = useState(false);
    const [serverError, setServerError] = useState('');
    const [pbIdsText, setPbIdsText] = useState(searchSettings.pbIds.join('\n'));

    const {setSuccessMessage} = useContext(MessageContext);

    const {
        data: searchData,
        setData: setSearchData,
        isChanged: isSearchChanged,
        saveChanges: createSearchAction,
    } = useEditSearch({initialData: {
        name: searchSettings.name,
        pbIds: searchSettings.pbIds,
        conferenceId: searchSettings.conferenceId,
        searchSettings: searchSettings.searchSettings,
    }, settingsId: searchSettings.id});

    const addSection = useCallback(async () => {
        setBusy(true);
        const response = await createSearchAction();

        if (isString(response)) {
            setServerError(response);
            setBusy(false);
        } else {
            setSuccessMessage('Search updated successfully');
            onAdd();
            onClose();
        }
    }, [createSearchAction, onAdd, onClose, setSuccessMessage]);

    const changeSearchName = useCallback((name: string) => {
        setSearchData(prev => ({...prev, name}));
    }, [setSearchData]);

    const changeSectionType = useCallback((newSettings: SearchSettingsType) => {
        setSearchData(prev => ({...prev, searchSettings: newSettings}));
    }, [setSearchData]);

    const changePBIds = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPbIdsText(event.target.value);
    }, []);

    const blurPBIds = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        const pbIds = event.currentTarget.value
            .trim()
            .split(/[\n,]+/)
            .filter(item => item);
        setSearchData(prev => ({...prev, pbIds}));
        setPbIdsText(pbIds.join('\n'));
    }, [setSearchData]);

    const isUpdateDisabled = useMemo(
        () => !searchData.name || !searchData.pbIds.length || !isSearchChanged,
        [searchData, isSearchChanged],
    );

    return (
        <>
            <DialogHeader onClose={onClose}>
                Update Search
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {serverError ? (
                    <Message color="error" onClose={() => setServerError('')}>
                        {serverError}
                    </Message>
                ) : null}
                <Group direction="v">
                    <FormInput
                        width="100%"
                        label="Search Name"
                        maxLength={128}
                        value={searchData.name}
                        onBlur={changeSearchName}
                    />
                    <WidthItem width="100%">
                        <Label>Search Type</Label>
                        <ComboBox value={searchData.searchSettings} onChange={changeSectionType}>
                            {Object.values(SearchSettingsTypes).map(searchType => (
                                <ComboboxItem key={searchType} caption={searchType} value={searchType}/>
                            ))}
                        </ComboBox>
                    </WidthItem>
                    <WidthItem width="100%">
                        <Label>PB IDs</Label>
                        <Textarea
                            value={pbIdsText}
                            placeholder="Enter PB IDs"
                            onChange={changePBIds}
                            onBlur={blurPBIds}
                        />
                    </WidthItem>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button busy={busy} variant="Type.PRIMARY" onClick={addSection} disabled={isUpdateDisabled}>
                        Update Search
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
